import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Hook for real-time speech recognition using Web Speech API
 * Provides word-by-word recognition with immediate feedback
 */
export function useRealtimeSpeechRecognition({ 
  onWord, 
  onError,
  language = 'ar-SA',
  continuous = true 
}) {
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const recognitionRef = useRef(null);
  const lastProcessedIndexRef = useRef(0);

  useEffect(() => {
    // Check if running on iOS/mobile
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isIOS || isMobile) {
      console.log('Mobile device detected - Web Speech API not reliable, streaming STT will be used instead');
      setIsSupported(false);
      // Don't show error - streaming STT will handle this automatically
      return;
    }
    
    // Check if Web Speech API is supported
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      console.error('Web Speech API not supported in this browser');
      setIsSupported(false);
      if (onError) {
        onError('Tarayıcınız ses tanımayı desteklemiyor. Lütfen Chrome veya Edge kullanın.');
      }
      return;
    }

    setIsSupported(true);

    // Initialize speech recognition
    const recognition = new SpeechRecognition();
    recognition.continuous = continuous;
    recognition.interimResults = true;
    recognition.lang = language;
    recognition.maxAlternatives = 1;

    // Handle results
    recognition.onresult = (event) => {
      const results = event.results;
      
      // Process only new results
      for (let i = lastProcessedIndexRef.current; i < results.length; i++) {
        const result = results[i];
        
        if (result.isFinal) {
          const transcript = result[0].transcript.trim();
          const words = transcript.split(/\s+/);
          
          // Send each word to callback
          words.forEach(word => {
            if (word && onWord) {
              onWord(word);
            }
          });
          
          lastProcessedIndexRef.current = i + 1;
        }
      }
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      
      if (event.error === 'no-speech') {
        // User stopped speaking, this is normal
        return;
      }
      
      if (event.error === 'aborted') {
        // Recognition was aborted, this is normal when stopping
        return;
      }
      
      setIsListening(false);
      
      if (onError) {
        const errorMessages = {
          'network': 'Ağ hatası. İnternet bağlantınızı kontrol edin.',
          'not-allowed': 'Mikrofon izni reddedildi. Lütfen izin verin.',
          'service-not-allowed': 'Ses tanıma servisi kullanılamıyor.',
          'bad-grammar': 'Dil tanıma hatası.',
          'language-not-supported': 'Arapça dil desteği yok.'
        };
        
        onError(errorMessages[event.error] || `Ses tanıma hatası: ${event.error}`);
      }
    };

    recognition.onend = () => {
      // If continuous mode and still listening, restart
      if (continuous && isListening) {
        try {
          recognition.start();
        } catch (e) {
          console.warn('Recognition restart failed:', e);
          setIsListening(false);
        }
      } else {
        setIsListening(false);
      }
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          console.warn('Error stopping recognition:', e);
        }
      }
    };
  }, [language, continuous, onWord, onError, isListening]); // Added isListening

  const startListening = useCallback(() => {
    if (!isSupported) {
      if (onError) {
        onError('Ses tanıma desteklenmiyor');
      }
      return;
    }

    if (recognitionRef.current && !isListening) {
      try {
        lastProcessedIndexRef.current = 0;
        recognitionRef.current.start();
        setIsListening(true);
      } catch (error) {
        console.error('Error starting recognition:', error);
        if (onError) {
          onError('Ses tanıma başlatılamadı');
        }
      }
    }
  }, [isSupported, isListening, onError]);

  const stopListening = useCallback(() => {
    if (recognitionRef.current && isListening) {
      try {
        recognitionRef.current.stop();
        setIsListening(false);
      } catch (error) {
        console.error('Error stopping recognition:', error);
      }
    }
  }, [isListening]);

  return {
    isListening,
    isSupported,
    startListening,
    stopListening
  };
}
